/**
 * PWA install-prompt safety net (Task #3328).
 *
 * Chrome fires `beforeinstallprompt` before showing the "Install app" banner.
 * Calling `e.preventDefault()` on it silences the prompt. This module
 * registers a global handler that suppresses the prompt on every page of the
 * main app and admin/partner console.
 *
 * Intentional exceptions — routes where the install prompt is allowed:
 *   - `/s/` — Share Spaces PWA.
 *
 * On those routes the event is NOT cancelled, so Chrome can still offer the
 * install banner. On every other route the event is cancelled and the
 * deferred prompt reference is discarded.
 *
 * Call `initPwaGuard()` once, as early as possible in the application entry
 * point (before the React tree renders), so the listener is in place before
 * Chrome has a chance to fire the event.
 */

const PWA_ALLOWED_PREFIXES = ["/s/"] as const;

function isAllowedPwaRoute(pathname: string): boolean {
  return PWA_ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function initPwaGuard(): void {
  if (typeof window === "undefined") return;

  window.addEventListener(
    "beforeinstallprompt",
    (e: Event) => {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "/";

      if (!isAllowedPwaRoute(pathname)) {
        e.preventDefault();
      }
    },
    { capture: true },
  );
}
